import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { X } from "lucide-react";

const SUGGESTED_TAGS = ["microsaas", "freelance", "hackathon", "workshops", "newsletter", "notiontemplates", "pod"];

export default function EditStrategy() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", summary: "", goal_amount: 0, tags: [] });
  const [customTag, setCustomTag] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStrategy() {
      if (!user) {
        nav("/login");
        return;
      }
      try {
        const { data, error } = await supabase
          .from("strategies")
          .select("*")
          .eq("id", id)
          .single();
        if (error) throw error;
        
        // Ensure user is author
        if (data.user_id !== user.id) {
          nav("/");
          return;
        }

        setForm({
          title: data.title,
          summary: data.summary,
          goal_amount: data.goal_amount,
          tags: data.tags || [],
        });
      } catch (e) {
        setErr("Could not load strategy.");
      } finally {
        setLoading(false);
      }
    }
    loadStrategy();
  }, [id, user, nav]);

  const addTag = (t) => {
    const clean = t.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!clean || form.tags.includes(clean)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, clean] }));
  };

  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const toggleSuggested = (t) => {
    if (form.tags.includes(t)) removeTag(t);
    else addTag(t);
  };

  const handleCustomTagKey = (e) => {
    if ((e.key === "Enter" || e.key === ",") && customTag.trim()) {
      e.preventDefault();
      addTag(customTag);
      setCustomTag("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { error } = await supabase.from("strategies").update({
        title:       form.title.trim(),
        summary:     form.summary.trim(),
        goal_amount: parseFloat(form.goal_amount || 0),
        tags:        form.tags,
      }).eq("id", id);
      
      if (error) throw error;
      nav(`/s/${id}`);
    } catch (e) {
      setErr(e.message || "Failed to update strategy");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-10 font-hand text-3xl">loading…</div>;
  }

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="font-hand text-marker-red text-3xl rotate-[-1deg] mb-1">— adjust the sails —</div>
      <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight mb-6">Edit strategy</h1>
      <form onSubmit={submit} className="brutal-card p-6 space-y-4" data-testid="edit-strategy-form">
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Title</div>
          <input className="brutal-input" required value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="strategy-title-input" />
        </label>
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Strategy details</div>
          <textarea className="brutal-input min-h-[160px]" required value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="strategy-summary-input" />
        </label>
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Target amount ($)</div>
          <input className="brutal-input" type="number" min="0" value={form.goal_amount}
            onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} data-testid="strategy-goal-input" />
        </label>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest mb-2">Tags</div>
          
          {/* Suggested tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTED_TAGS.map((t) => (
              <button key={t} type="button" onClick={() => toggleSuggested(t)} className="tag-pill"
                style={{ background: form.tags.includes(t) ? "#FF3333" : "#FFD600", color: form.tags.includes(t) ? "#fff" : "#121212" }}
                data-testid={`tag-toggle-${t}`}>
                #{t}
              </button>
            ))}
          </div>

          {/* Custom tag input */}
          <div className="flex gap-2 mb-3">
            <input
              className="brutal-input flex-1"
              placeholder="Type a custom tag and press Enter…"
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={handleCustomTagKey}
              data-testid="custom-tag-input"
            />
            <button
              type="button"
              onClick={() => { if (customTag.trim()) { addTag(customTag); setCustomTag(""); } }}
              className="brutal-btn brutal-btn-yellow text-xs"
            >
              Add
            </button>
          </div>

          {/* Selected tags display */}
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.tags.map((t) => (
                <span key={t} className="tag-pill flex items-center gap-1"
                  style={{ background: "#121212", color: "#fff" }}>
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-marker-red">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {err && <div className="border-2 border-marker-red bg-marker-red/10 p-3 font-mono text-sm">{err}</div>}
        <button type="submit" disabled={busy} className="brutal-btn w-full" data-testid="strategy-submit-btn">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </form>
    </main>
  );
}
